import { LIMIT_FOR_MINUTE_AGGREGATION_MILLISECONDS } from "@queries/useGetTimeseriesQuery"
import { useQuery } from "@tanstack/react-query"
import { fetchTimeseriesDataInBatches } from "@utils/queries/timeseriesBatching"
import { endpoints } from "api"

type GetTimeseries4QueryArgs = {
	gte?: Date
	lt?: Date
	sensorIds?: (number | null)[]
}

export function useGetTimeseries4Query(args: GetTimeseries4QueryArgs) {
	return useQuery({
		queryKey: [
			`queryId=GetTimeseries4Query`,
			`endpoints=[${endpoints.getTimeseriesData4.getName()}]`,
			`sensorIds=[${args.sensorIds}]`,
			`gte=${
				args.gte?.toISOString !== undefined
					? `${args.gte.toISOString().split(":")[0]}:${
							args.gte.toISOString().split(":")[1]
						}`
					: args.gte
			}`,
			`lt=${
				args.lt?.toISOString !== undefined
					? `${args.lt.toISOString().split(":")[0]}:${
							args.lt.toISOString().split(":")[1]
						}`
					: args.lt
			}`,
		],
		queryFn: async ({
			signal,
		}): Promise<{
			sensors: {
				data: [number, number][]
				id: number
			}[]
		} | null> => {
			if (
				args.gte === undefined ||
				args.lt === undefined ||
				args.sensorIds === undefined ||
				args.sensorIds.length === 0
			) {
				throw new Error(
					"Invalid query params - gte, lt and at least one sensorId must be provided"
				)
			}
			const response = await fetchTimeseriesDataInBatches(
				{
					interval: { gte: args.gte, lt: args.lt },
					sensorIds: args.sensorIds.filter((s): s is number => s !== null),
					aggregation:
						new Date(args.lt).getTime() - new Date(args.gte).getTime() >
						LIMIT_FOR_MINUTE_AGGREGATION_MILLISECONDS
							? {
									groupBy: "hour",
									method: "average",
								}
							: undefined,
				},
				signal
			)

			return {
				sensors: response.sensors.map((s) => {
					return {
						data: s.data.map((d) => {
							return [d[0].getTime(), d[1]]
						}),
						id: s.id,
					}
				}),
			}
		},
		enabled:
			args.gte !== undefined &&
			args.lt !== undefined &&
			args.sensorIds !== undefined &&
			args.sensorIds.length > 0,
		staleTime: 10 * 60 * 1000,
		refetchInterval: 10 * 60 * 1000,
	})
}
